import type { GroomingBatchListEntry, GroomingProjectSession } from "@/lib/api/grooming";

/**
 * Which prepared agenda run the Session screen works with (epic
 * PI-SPEC-SW-E280), in one place so the Session screen and the icon rail's
 * "ready to post" badge (UX-PM-32, epic PI-SPEC-SW-E287) read the same
 * definition. Pure.
 */

/** Today as `YYYY-MM-DD` in the browser's time zone. */
export function localToday(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** `YYYY-MM-DD` as a local Date at midnight. */
export function dateParts(date: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** `YYYY-MM-DD` shifted by whole days. */
export function addDays(date: string, days: number): string {
  const d = dateParts(date);
  d.setDate(d.getDate() + days);
  return localToday(d);
}

const RUN_STATUSES: ReadonlySet<GroomingBatchListEntry["status"]> = new Set(["running", "stopped", "completed"]);

/** The project's newest agenda batch that was prepared (running, stopped or finished), if any. */
export function latestAgendaRun(batches: readonly GroomingBatchListEntry[] | undefined): GroomingBatchListEntry | undefined {
  return batches?.find((batch) => batch.mode === "agenda" && RUN_STATUSES.has(batch.status));
}

/**
 * Whether a prepared agenda still belongs to the coming session: its own date
 * is today or later, or (without one) it was prepared after the previous
 * meeting of the pattern. Without a pattern, a running or stopped run is
 * current and a finished one is not.
 */
export function isCurrentRun(
  run: Pick<GroomingBatchListEntry, "status" | "sessionDate" | "startedAt" | "createdAt">,
  session: Pick<GroomingProjectSession, "meetingPattern" | "nextMeetingDate">,
  today: string
): boolean {
  if (run.sessionDate) return run.sessionDate >= today;
  const pattern = session.meetingPattern;
  if (!pattern || !session.nextMeetingDate) return run.status !== "completed";
  const previousMeeting = addDays(session.nextMeetingDate, -7 * pattern.everyNWeeks);
  const prepared = localToday(new Date(run.startedAt ?? run.createdAt));
  return prepared > previousMeeting;
}

/**
 * The prepared agenda run of the coming session (Open workspace), else
 * undefined: the newest prepared agenda run when it still belongs to the
 * coming session. When the next meeting's agenda was prepared early too, the
 * run for the earliest meeting wins, so it never hides the meeting before it
 * (PKG-PM-C Mac walk, 2026-10-23).
 */
export function currentAgendaRun(
  batches: readonly GroomingBatchListEntry[] | undefined,
  session: Pick<GroomingProjectSession, "meetingPattern" | "nextMeetingDate"> | undefined,
  today: string
): GroomingBatchListEntry | undefined {
  const run = latestAgendaRun(batches);
  if (!run || !session || !isCurrentRun(run, session, today)) return undefined;
  let earliest = run;
  for (const other of batches ?? []) {
    if (other.mode !== "agenda" || !RUN_STATUSES.has(other.status) || !isCurrentRun(other, session, today)) continue;
    const date = runMeetingDate(other, session);
    const best = runMeetingDate(earliest, session);
    if (date !== null && best !== null && date < best) earliest = other;
  }
  return earliest;
}

/**
 * The prepared run of a later meeting than `current` (the next meeting's
 * agenda prepared early), the earliest such; undefined without one
 * (PKG-PM-C Mac walk, 2026-10-23).
 */
export function laterAgendaRun(
  batches: readonly GroomingBatchListEntry[] | undefined,
  session: Pick<GroomingProjectSession, "meetingPattern" | "nextMeetingDate"> | undefined,
  today: string,
  current: GroomingBatchListEntry | undefined
): GroomingBatchListEntry | undefined {
  if (!current || !session) return undefined;
  const after = runMeetingDate(current, session);
  if (after === null) return undefined;
  let later: GroomingBatchListEntry | undefined;
  let laterDate: string | null = null;
  for (const run of batches ?? []) {
    if (run.id === current.id || run.mode !== "agenda" || !RUN_STATUSES.has(run.status) || !isCurrentRun(run, session, today)) continue;
    const date = runMeetingDate(run, session);
    if (date !== null && date > after && (laterDate === null || date < laterDate)) {
      later = run;
      laterDate = date;
    }
  }
  return later;
}

/** The meeting a prepared run is for: its own date, else the project's next meeting; null without either. */
export function runMeetingDate(
  run: Pick<GroomingBatchListEntry, "sessionDate">,
  session: Pick<GroomingProjectSession, "nextMeetingDate">
): string | null {
  return run.sessionDate ?? session.nextMeetingDate ?? null;
}

/**
 * Whether the project's shared draft agenda is for a later meeting than `run`
 * (PKG-PM-C Mac walk, 2026-10-23): a draft started with "Start the next
 * meeting's agenda", or made while the run's agenda was set. That draft must
 * not take the Session screen over before the run's meeting has happened.
 */
export function isLaterAgenda(
  session: Pick<GroomingProjectSession, "batch" | "sessionDate" | "nextMeetingDate">,
  run: Pick<GroomingBatchListEntry, "sessionDate">
): boolean {
  if (!session.batch || !session.sessionDate) return false;
  const runDate = runMeetingDate(run, session);
  return runDate !== null && session.sessionDate > runDate;
}

/**
 * The run whose agenda the Session screen shows and whose held briefs make its
 * "Post all (N)": the current agenda run, while the project has no shared
 * draft agenda or only one for a later meeting (a draft for the same meeting
 * takes the build step over). Undefined otherwise, so the icon rail's badge is
 * 0 exactly when the Session screen offers no Post all.
 */
export function readyToPostRun(
  batches: readonly GroomingBatchListEntry[] | undefined,
  session: Pick<GroomingProjectSession, "batch" | "sessionDate" | "meetingPattern" | "nextMeetingDate"> | undefined,
  today: string
): GroomingBatchListEntry | undefined {
  if (!session) return undefined;
  const run = currentAgendaRun(batches, session, today);
  if (!run || (session.batch && !isLaterAgenda(session, run))) return undefined;
  return run;
}
